import { useState } from 'react';
import {
  ArrowPathIcon,
  BanknotesIcon,
  BoltIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  KeyIcon,
  PuzzlePieceIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  ContentLoader,
  EmptyState,
  Input,
  Pagination,
  Select,
} from '@/components/common';
import {
  useAdminApiLogs,
  useAdminBrokerCredentialStatus,
  useAdminCircuitBreakers,
  useAdminCreateIntegrationConfig,
  useAdminIntegrationConfigs,
  useAdminResetCircuitBreaker,
  useAdminTestConnection,
  useAdminUpdateIntegrationConfig,
  useActivationRequests,
  useBrokerLookupPlans,
  useLookupVendors,
  useSaveBrokerLookupPlan,
  useSaveLookupVendor,
} from '@/hooks/useIntegrations';
import { useInsurersLookup } from '@/hooks/useInsurers';
import {
  describeTestStage,
  summariseEgress,
  type AdminAPILogsParams,
  type EgressSummary,
  type IntegrationEnv,
} from '@/services/integrations';
import { InsurerPluginsTab } from './integrations/InsurerPluginsTab';
import { formatDate, formatDateTime } from '@/utils';

const TABS = [
  { id: 'configs', label: 'Insurer Integrations', icon: PuzzlePieceIcon },
  { id: 'plugins', label: 'Insurer Plug-ins', icon: CpuChipIcon },
  { id: 'onboarding', label: 'Broker Onboarding', icon: KeyIcon },
  { id: 'logs', label: 'API Logs', icon: DocumentMagnifyingGlassIcon },
  { id: 'breakers', label: 'Circuit Breakers', icon: BoltIcon },
  { id: 'test', label: 'Test Connection', icon: SignalIcon },
  { id: 'vendors', label: 'Lookup Vendors', icon: KeyIcon },
  { id: 'plans', label: 'Lookup Billing', icon: BanknotesIcon },
] as const;

type TabId = (typeof TABS)[number]['id'];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
  { value: 'timeout', label: 'Timeout' },
];

const ENVIRONMENT_OPTIONS = [
  { value: '', label: 'All environments' },
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];

const TEST_ENV_OPTIONS = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];

const BREAKER_STATE_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  closed: 'success',
  half_open: 'warning',
  open: 'error',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  success: 'success',
  error: 'error',
  timeout: 'warning',
};

export function IntegrationsAdminPage() {
  const [activeTab, setActiveTab] = useState<TabId>('configs');

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Monitor insurer API traffic, circuit breakers, and adapter health"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Integrations' }]}
      />

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'configs' && <IntegrationConfigsTab />}
        {activeTab === 'plugins' && <InsurerPluginsTab />}
        {activeTab === 'onboarding' && <BrokerOnboardingTab />}
        {activeTab === 'logs' && <ApiLogsTab />}
        {activeTab === 'breakers' && <CircuitBreakersTab />}
        {activeTab === 'test' && <TestConnectionTab />}
        {activeTab === 'vendors' && <LookupVendorsTab />}
        {activeTab === 'plans' && <LookupPlansTab />}
      </div>
    </div>
  );
}

/**
 * Where insurer traffic actually leaves from.
 *
 * Read-only and secret-free by construction. The relay's URL and key are
 * server-side settings (INSURER_RELAY_URL / INSURER_RELAY_KEY in
 * backend/.env); they are never serialised to the browser and there is
 * deliberately no control here to edit them. What a country admin needs is the
 * single fact that changes how every failure below is read: did the call go
 * straight out of this box, or through the relay, and via which host.
 *
 * When the API says nothing, this says "unknown". It never says "direct" on a
 * guess — a silently-assumed direct route is how a relay outage stays
 * invisible while quotes quietly stop pricing.
 */
function EgressStatus({
  summary,
  isLoading,
}: {
  summary: EgressSummary;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Reading how insurer traffic leaves this server…
        </p>
      </Card>
    );
  }

  const badge: { variant: 'info' | 'default' | 'warning'; text: string } =
    summary.mode === 'relay'
      ? { variant: 'info', text: 'Via relay' }
      : summary.mode === 'direct'
        ? { variant: 'default', text: 'Direct' }
        : summary.mode === 'mixed'
          ? { variant: 'warning', text: 'Mixed' }
          : { variant: 'warning', text: 'Unknown' };

  return (
    <Card>
      <div
        data-testid="egress-status"
        className="flex flex-wrap items-start justify-between gap-3"
      >
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Insurer egress
          </h3>

          {summary.mode === 'relay' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Insurer calls leave through the relay at{' '}
              <span data-testid="egress-host" className="font-mono text-gray-700 dark:text-gray-300">
                {summary.host ?? 'a host the server did not name'}
              </span>
              . Its address and key stay on the server and are never shown or edited here.
            </p>
          )}

          {summary.mode === 'direct' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Insurer calls leave straight from this application server, so an insurer
              that filters by geography sees this server&apos;s own address.
            </p>
          )}

          {summary.mode === 'unknown' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This API build does not report how insurer traffic leaves the server, so it
              cannot be shown. Treat the route as unknown rather than assuming direct.
            </p>
          )}

          {summary.mode === 'mixed' && (
            <>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                The integrations listed report more than one route:
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                {summary.routes.map((route) => (
                  <li key={`${route.mode}:${route.host ?? ''}`}>
                    {route.mode === 'relay' ? (
                      <>
                        via relay{' '}
                        <span className="font-mono text-gray-700 dark:text-gray-300">
                          {route.host ?? 'host not reported'}
                        </span>
                      </>
                    ) : (
                      'direct'
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <span data-testid="egress-mode" className="shrink-0">
          <Badge variant={badge.variant} size="sm">
            {badge.text}
          </Badge>
        </span>
      </div>
    </Card>
  );
}

function ApiLogsTab() {
  const [page, setPage] = useState(1);
  const [insurer, setInsurer] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [environment, setEnvironment] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: insurers } = useInsurersLookup();

  const params: AdminAPILogsParams = { page };
  if (insurer) params.insurer = insurer;
  if (statusFilter) params.status = statusFilter;
  if (environment) params.environment = environment;
  if (dateFrom) params.started_at__gte = dateFrom;
  if (dateTo) params.started_at__lte = dateTo;

  const { data, isLoading } = useAdminApiLogs(params);

  const resetPage = () => setPage(1);

  const insurerOptions = [
    { value: '', label: 'All insurers' },
    ...(insurers ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];

  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Select
            options={insurerOptions}
            value={insurer}
            onChange={(e) => {
              setInsurer(e.target.value);
              resetPage();
            }}
          />
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              resetPage();
            }}
          />
          <Select
            options={ENVIRONMENT_OPTIONS}
            value={environment}
            onChange={(e) => {
              setEnvironment(e.target.value);
              resetPage();
            }}
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPage();
            }}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPage();
            }}
          />
        </div>
      </Card>

      {isLoading ? (
        <ContentLoader />
      ) : !data?.results?.length ? (
        <Card>
          <EmptyState
            icon={<DocumentMagnifyingGlassIcon />}
            title="No API logs"
            description="No calls match the current filters."
          />
        </Card>
      ) : (
        <>
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Insurer</th>
                    <th className="px-4 py-3">Broker</th>
                    <th className="px-4 py-3">Endpoint</th>
                    <th className="px-4 py-3">Env</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Latency</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  {data.results.map((log) => (
                    <tr key={log.id} className="text-gray-700 dark:text-gray-300">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateTime(log.started_at)}
                      </td>
                      <td className="px-4 py-3">{log.insurer_name || log.insurer_code}</td>
                      <td className="px-4 py-3">{log.broker_name || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{log.endpoint_name}</td>
                      <td className="px-4 py-3">{log.environment}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={STATUS_VARIANT[log.status] || 'default'}
                          size="sm"
                        >
                          {log.status}
                          {log.response_status_code ? ` · ${log.response_status_code}` : ''}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {log.latency_ms != null ? `${log.latency_ms} ms` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {data.total_pages > 1 && (
            <Pagination
              currentPage={data.current_page}
              totalPages={data.total_pages}
              totalItems={data.count}
              itemsPerPage={data.page_size}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

function CircuitBreakersTab() {
  const { data, isLoading } = useAdminCircuitBreakers();
  const resetMutation = useAdminResetCircuitBreaker();

  if (isLoading) return <ContentLoader />;

  if (!data?.results?.length) {
    return (
      <Card>
        <EmptyState
          icon={<BoltIcon />}
          title="No circuit breakers configured"
          description="Breakers appear once an insurer integration has been set up."
        />
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {data.results.map((cb) => {
        const variant = BREAKER_STATE_VARIANT[cb.current_state] || 'info';
        const isResetting =
          resetMutation.isPending && resetMutation.variables === cb.insurer_code;
        return (
          <Card key={cb.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {cb.insurer_name}
                  </h3>
                  <Badge variant={variant} size="sm">
                    {cb.current_state.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {cb.environment} · {cb.endpoint_name || 'all endpoints'}
                </p>
              </div>
              {!cb.is_enabled && (
                <Badge variant="default" size="sm">
                  disabled
                </Badge>
              )}
            </div>

            <dl className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Threshold</dt>
                <dd className="font-mono text-gray-900 dark:text-white">
                  {cb.failure_threshold}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Window</dt>
                <dd className="font-mono text-gray-900 dark:text-white">
                  {cb.failure_window_secs}s
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Cooldown</dt>
                <dd className="font-mono text-gray-900 dark:text-white">
                  {cb.cooldown_secs}s
                </dd>
              </div>
            </dl>

            <div className="mt-4 flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => resetMutation.mutate(cb.insurer_code)}
                disabled={isResetting}
                leftIcon={<ArrowPathIcon className="h-4 w-4" />}
              >
                {isResetting ? 'Resetting…' : 'Reset'}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function TestConnectionTab() {
  const { data: insurers, isLoading: insurersLoading } = useInsurersLookup();
  const [insurerCode, setInsurerCode] = useState('');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const mutation = useAdminTestConnection();
  // The egress route is half the diagnosis of any failure here: an insurer
  // that filters by geography answers nothing at all when the call leaves from
  // the wrong country, and that reads identically to a dead host.
  const { data: configs, isLoading: configsLoading } = useAdminIntegrationConfigs();

  const insurerOptions = [
    { value: '', label: 'Select an insurer…' },
    ...(insurers ?? []).map((i) => ({ value: i.code, label: `${i.name} (${i.code})` })),
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!insurerCode) return;
    mutation.mutate({ insurerCode, environment });
  };

  const result = mutation.data;
  const resultOk = result?.status === 'ok';
  // Never derived from `status`: which leg the call stopped at is the server's
  // to report, and an absent or unfamiliar stage is shown as exactly that.
  const stage = result ? describeTestStage(result.stage) : null;

  return (
    <div className="space-y-4">
    <EgressStatus summary={summariseEgress(configs?.results)} isLoading={configsLoading} />
    <Card>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Insurer
          </label>
          <Select
            options={insurerOptions}
            value={insurerCode}
            onChange={(e) => setInsurerCode(e.target.value)}
            disabled={insurersLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Environment
          </label>
          <Select
            options={TEST_ENV_OPTIONS}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
          />
        </div>

        <Button
          type="submit"
          data-testid="run-test-connection"
          disabled={!insurerCode || mutation.isPending}
          leftIcon={<SignalIcon className="h-4 w-4" />}
        >
          {mutation.isPending ? 'Testing…' : 'Test connection'}
        </Button>

        {result && stage && (
          <div
            data-testid="test-connection-result"
            className={`rounded-lg border p-4 text-sm ${
              resultOk
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={resultOk ? 'success' : 'error'} size="sm">
                {result.status}
              </Badge>
              <span className="font-medium">
                {result.insurer_code} · {result.environment}
              </span>
              <span data-testid="test-stage" className="font-mono text-xs">
                stage: {stage.stage}
                {result.http_status != null ? ` · HTTP ${result.http_status}` : ''}
              </span>
            </div>
            <p data-testid="test-stage-label" className="mt-2 font-medium">
              {stage.label}
            </p>
            <p data-testid="test-stage-meaning" className="mt-1 text-xs">
              {stage.meaning}
            </p>
            {stage.nextStep && <p className="mt-1 text-xs">{stage.nextStep}</p>}
            {result.message && (
              <p className="mt-2 font-mono text-xs break-all">{result.message}</p>
            )}
            {result.error && (
              <p className="mt-2 font-mono text-xs break-all">{result.error}</p>
            )}
            {result.adapter && (
              <p className="mt-2 font-mono text-xs">Adapter: {result.adapter}</p>
            )}
          </div>
        )}
      </form>
    </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Insurer integrations — the switch that makes an insurer available to brokers
// ---------------------------------------------------------------------------

/** Brokers waiting on an insurer integration being switched on. */
function ActivationRequestQueue({
  onPrefill,
}: {
  onPrefill: (insurerId: string, environment: IntegrationEnv) => void;
}) {
  const { data, isLoading } = useActivationRequests();
  const requests = data?.results ?? [];

  if (isLoading || requests.length === 0) return null;

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Requested activations
          <Badge variant="warning" size="sm" className="ml-2">
            {requests.length}
          </Badge>
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Brokers waiting on an insurer being switched on. Choosing one fills the form
          below; you still set the endpoint and adapter version.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <th className="px-4 py-2">Broker</th>
              <th className="px-4 py-2">Insurer</th>
              <th className="px-4 py-2">Environment</th>
              <th className="px-4 py-2">Requested</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {requests.map((req) => (
              <tr key={req.id} className="text-sm">
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{req.broker_name}</td>
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                  {req.insurer_name}
                  <span className="ml-2 text-xs text-gray-500">{req.insurer_code}</span>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{req.environment}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                  {req.created_at ? formatDate(req.created_at) : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPrefill(req.insurer_id, req.environment)}
                  >
                    Set up
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function IntegrationConfigsTab() {
  const [environment, setEnvironment] = useState<IntegrationEnv | ''>('');
  const { data, isLoading } = useAdminIntegrationConfigs(environment || undefined);
  const { data: insurers } = useInsurersLookup();
  const createConfig = useAdminCreateIntegrationConfig();
  const updateConfig = useAdminUpdateIntegrationConfig();

  const [newInsurer, setNewInsurer] = useState('');
  const [newEnv, setNewEnv] = useState<IntegrationEnv>('sandbox');
  const [newVersion, setNewVersion] = useState('');
  const [newBaseUrl, setNewBaseUrl] = useState('');
  const [editingUrlFor, setEditingUrlFor] = useState<string | null>(null);
  const [draftUrl, setDraftUrl] = useState('');
  // Service hosts are an open-ended map: an insurer may front payment, PDF,
  // KYC or anything else with its own hostname, and the adapter decides which
  // names it looks for. Editing them as rows keeps the UI from having to know.
  const [draftServices, setDraftServices] = useState<Array<{ name: string; url: string }>>([]);

  const configs = data?.results ?? [];

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInsurer || !newVersion) return;
    createConfig.mutate(
      {
        insurer: newInsurer,
        environment: newEnv,
        active_api_version: newVersion,
        base_url: newBaseUrl.trim(),
        is_enabled: true,
      },
      { onSuccess: () => { setNewInsurer(''); setNewVersion(''); setNewBaseUrl(''); } },
    );
  };

  return (
    <div className="space-y-6">
      <EgressStatus summary={summariseEgress(configs)} isLoading={isLoading} />

      <ActivationRequestQueue
        onPrefill={(insurerId, environment) => {
          // The admin still supplies the endpoint and the adapter version --
          // that is the whole reason a broker files a request instead of
          // configuring this themselves. This only saves them the typing.
          setNewInsurer(insurerId);
          setNewEnv(environment);
        }}
      />

      <Card>
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Enable an insurer integration
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Turning an integration on makes the insurer visible to brokers, who then
            enter their own API key under Broker → API Credentials. The adapter version
            must already be implemented — enabling a version with no adapter is rejected.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The API endpoint you set here is used by every broker on this insurer —
            brokers never enter a URL. It may be a private address if the insurer
            serves its partner API over a VPN or peered network.
          </p>
        </div>
        <form onSubmit={handleCreate} className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <Select
            options={[
              { value: '', label: 'Select insurer…' },
              ...(insurers ?? []).map((i) => ({ value: i.id, label: `${i.name} (${i.code})` })),
            ]}
            value={newInsurer}
            onChange={(e) => setNewInsurer(e.target.value)}
          />
          <Select
            options={TEST_ENV_OPTIONS}
            value={newEnv}
            onChange={(e) => setNewEnv(e.target.value as IntegrationEnv)}
          />
          <Input
            placeholder="Adapter version, e.g. v2.1"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
          />
          <Input
            placeholder="API endpoint, e.g. https://api.godigit.com"
            value={newBaseUrl}
            onChange={(e) => setNewBaseUrl(e.target.value)}
          />
          <Button type="submit" disabled={!newInsurer || !newVersion || createConfig.isPending}>
            {createConfig.isPending ? 'Enabling…' : 'Enable'}
          </Button>
        </form>
      </Card>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Configured integrations
          </h3>
          <div className="w-48">
            <Select
              options={ENVIRONMENT_OPTIONS}
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as IntegrationEnv | '')}
            />
          </div>
        </div>

        {isLoading ? (
          <ContentLoader />
        ) : configs.length === 0 ? (
          <EmptyState
            title="No integrations configured"
            description="Enable an insurer above to let brokers start entering their credentials."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Insurer</th>
                  <th className="px-4 py-3">Environment</th>
                  <th className="px-4 py-3">Adapter version</th>
                  <th className="px-4 py-3">API endpoint</th>
                  <th className="px-4 py-3">Adapter</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {configs.map((config) => (
                  <tr key={config.id} className="text-sm">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {config.insurer_name}
                      </div>
                      <div className="text-xs text-gray-500">{config.insurer_code}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{config.environment}</td>
                    <td className="px-4 py-3">{config.active_api_version}</td>
                    <td className="px-4 py-3">
                      {editingUrlFor === config.id ? (
                        <div className="space-y-2 min-w-[22rem]">
                          <Input
                            label="Base URL (quotes)"
                            placeholder="https://qnb.insurer.com"
                            value={draftUrl}
                            onChange={(e) => setDraftUrl(e.target.value)}
                          />
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Service hosts (optional)
                            </label>
                            <p className="text-xs text-gray-500">
                              Only for services this insurer serves from a different
                              hostname. Anything not listed uses the base URL.
                            </p>
                            {draftServices.map((row, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Input
                                  className="w-32"
                                  placeholder="payment"
                                  value={row.name}
                                  onChange={(e) =>
                                    setDraftServices((rows) =>
                                      rows.map((r, j) =>
                                        j === i ? { ...r, name: e.target.value } : r,
                                      ),
                                    )
                                  }
                                />
                                <Input
                                  className="flex-1"
                                  placeholder="https://payments.insurer.com"
                                  value={row.url}
                                  onChange={(e) =>
                                    setDraftServices((rows) =>
                                      rows.map((r, j) =>
                                        j === i ? { ...r, url: e.target.value } : r,
                                      ),
                                    )
                                  }
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setDraftServices((rows) => rows.filter((_, j) => j !== i))
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                setDraftServices((rows) => [...rows, { name: '', url: '' }])
                              }
                            >
                              Add service host
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              disabled={updateConfig.isPending}
                              onClick={() => {
                                const services: Record<string, string> = {};
                                draftServices.forEach(({ name, url }) => {
                                  if (name.trim() && url.trim()) services[name.trim()] = url.trim();
                                });
                                updateConfig.mutate(
                                  {
                                    id: config.id,
                                    data: {
                                      base_url: draftUrl.trim(),
                                      service_endpoints: services,
                                    },
                                  },
                                  { onSuccess: () => setEditingUrlFor(null) },
                                );
                              }}
                            >
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingUrlFor(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="text-left cursor-pointer hover:underline"
                          onClick={() => {
                            setEditingUrlFor(config.id);
                            setDraftUrl(config.base_url ?? '');
                            setDraftServices(
                              Object.entries(config.service_endpoints ?? {}).map(
                                ([name, url]) => ({ name, url }),
                              ),
                            );
                          }}
                        >
                          {config.base_url ? (
                            <div className="space-y-0.5">
                              <div className="font-mono text-xs break-all">{config.base_url}</div>
                              {Object.entries(config.service_endpoints ?? {}).map(([svc, host]) => (
                                <div key={svc} className="text-[11px] text-gray-500 break-all">
                                  <span className="uppercase tracking-wide">{svc}</span>
                                  {': '}
                                  <span className="font-mono">{host}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Badge variant="warning" size="sm">Not set</Badge>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {config.adapter_registered ? (
                        <Badge variant="success" size="sm">Registered</Badge>
                      ) : (
                        <Badge variant="error" size="sm">Missing</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {config.is_enabled ? (
                        <Badge variant="success" size="sm">Enabled</Badge>
                      ) : (
                        <Badge variant="default" size="sm">Disabled</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={updateConfig.isPending}
                        onClick={() =>
                          updateConfig.mutate({
                            id: config.id,
                            data: { is_enabled: !config.is_enabled },
                          })
                        }
                      >
                        {config.is_enabled ? 'Disable' : 'Enable'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Broker onboarding — who has entered their insurer keys
// ---------------------------------------------------------------------------

function BrokerOnboardingTab() {
  const [environment, setEnvironment] = useState<IntegrationEnv | ''>('');
  const { data, isLoading } = useAdminBrokerCredentialStatus(
    environment ? { environment } : {},
  );
  const rows = data?.results ?? [];

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Broker credential status
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Which brokers have entered their own insurer API keys. Key material is never
            shown here — each broker holds their own credentials.
          </p>
        </div>
        <div className="w-48">
          <Select
            options={ENVIRONMENT_OPTIONS}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as IntegrationEnv | '')}
          />
        </div>
      </div>

      {isLoading ? (
        <ContentLoader />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No broker credentials yet"
          description="Once a broker saves their insurer API key it will appear here."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Broker</th>
                <th className="px-4 py-3">Insurer</th>
                <th className="px-4 py-3">Environment</th>
                <th className="px-4 py-3">Configured</th>
                <th className="px-4 py-3">Last test</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((row) => (
                <tr key={row.id} className="text-sm">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {row.broker_name}
                  </td>
                  <td className="px-4 py-3">{row.insurer_name}</td>
                  <td className="px-4 py-3 capitalize">{row.environment}</td>
                  <td className="px-4 py-3">
                    {row.is_configured ? (
                      <Badge variant="success" size="sm">Yes</Badge>
                    ) : (
                      <Badge variant="default" size="sm">No</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.last_tested_at ? (
                      <Badge variant={row.last_test_ok ? 'success' : 'error'} size="sm">
                        {row.last_test_ok ? 'Passed' : 'Failed'}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">Never tested</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Lookup vendors
// ---------------------------------------------------------------------------

/** Vendors the platform buys vehicle registration lookups from.
 *
 *  Country-admin only on purpose: a broker searches a registration and is
 *  billed for it, but which vendor answered, in what order they are tried and
 *  what each costs the platform are decisions above the broker.
 *
 *  Secrets are write-only. The API returns a mask or a boolean and never the
 *  value, and an empty field on save means "keep the stored one" — so changing
 *  a priority or switching a vendor off cannot wipe its credentials.
 */
function LookupVendorsTab() {
  const { data, isLoading } = useLookupVendors();
  const save = useSaveLookupVendor();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const vendors = data?.results ?? [];

  const field = (key: string) => ({
    value: draft[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((d) => ({ ...d, [key]: e.target.value })),
  });

  if (isLoading) return <ContentLoader />;

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Vehicle lookup vendors
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          The platform holds one account with each vendor, pays it, and bills brokers
          per search. Brokers never see or choose a vendor. Vendors are tried in
          priority order and the first that answers wins.
        </p>
      </div>

      {vendors.length === 0 ? (
        <EmptyState
          title="No lookup vendors"
          description="Add a vendor to enable registration lookups."
        />
      ) : (
        <div className="space-y-4">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{vendor.name}</span>
                    <Badge variant="default" size="sm">{vendor.environment}</Badge>
                    {vendor.is_configured ? (
                      <Badge variant="success" size="sm">Configured</Badge>
                    ) : (
                      <Badge variant="warning" size="sm">Needs credentials</Badge>
                    )}
                    {!vendor.is_enabled && <Badge variant="default" size="sm">Disabled</Badge>}
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                    <div className="font-mono break-all">{vendor.base_url || 'no base URL'}</div>
                    <div>
                      API key {vendor.api_key_masked || '—'} · secret{' '}
                      {vendor.has_api_secret ? 'stored' : 'missing'} · private key{' '}
                      {vendor.has_private_key ? 'stored' : 'missing'} · priority {vendor.priority}
                      {' · '}₹{vendor.cost_per_lookup}/lookup to us
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ id: vendor.id, data: { is_enabled: !vendor.is_enabled } })
                    }
                  >
                    {vendor.is_enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(editing === vendor.id ? null : vendor.id);
                      setDraft({});
                    }}
                  >
                    {editing === vendor.id ? 'Cancel' : 'Credentials'}
                  </Button>
                </div>
              </div>

              {editing === vendor.id && (
                <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
                  <p className="text-xs text-gray-500">
                    Leave a field blank to keep what is already stored. Values are
                    encrypted at rest and never shown again.
                  </p>
                  <Input label="API key" type="password" placeholder="from the vendor's developer portal" {...field('api_key')} />
                  <Input label="Secret key" type="password" placeholder="paired with the API key" {...field('api_secret')} />
                  <Input label="Base URL" placeholder={vendor.base_url} {...field('base_url')} />
                  <Input label="Token URL" placeholder={vendor.token_url} {...field('token_url')} />
                  <Input label="Cost per lookup (what the vendor charges us)" placeholder={vendor.cost_per_lookup} {...field('cost_per_lookup')} />
                  <Button
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => {
                      const payload = Object.fromEntries(
                        Object.entries(draft).filter(([, v]) => v.trim() !== ''),
                      );
                      save.mutate(
                        { id: vendor.id, data: payload },
                        { onSuccess: () => { setEditing(null); setDraft({}); } },
                      );
                    }}
                  >
                    Save credentials
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * What a registration search costs each broker.
 *
 * The gate for the whole paid journey: a broker with no plan, or a plan
 * switched off here, cannot buy a search at all. Every broker gets a row when
 * the feature ships, disabled and priced at zero, so nothing starts billing
 * by accident and turning one on is a deliberate act.
 *
 * The two public columns are read-only here on purpose. Whether an anonymous
 * visitor may spend a broker's money is the broker's call; switching
 * `is_enabled` off already stops every paid search including those, so the
 * emergency lever exists without taking the decision away from its owner.
 */
function LookupPlansTab() {
  const { data, isLoading } = useBrokerLookupPlans();
  const save = useSaveBrokerLookupPlan();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const plans = data?.results ?? [];

  const field = (key: string) => ({
    value: draft[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((d) => ({ ...d, [key]: e.target.value })),
  });

  if (isLoading) return <ContentLoader />;

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Broker lookup billing
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Only a vendor call is billable — a vehicle already in our registry is free
          for every broker, however it is reached. Prepaid refuses a search the
          balance will not cover; postpaid lets the charge accrue and run negative.
        </p>
      </div>

      {plans.length === 0 ? (
        <EmptyState
          title="No lookup plans"
          description="No broker in your country can buy a registration search yet."
        />
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <div key={plan.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {plan.broker_name}
                    </span>
                    <Badge variant="default" size="sm">{plan.broker_code}</Badge>
                    {plan.is_enabled ? (
                      <Badge variant="success" size="sm">Enabled</Badge>
                    ) : (
                      <Badge variant="default" size="sm">Disabled</Badge>
                    )}
                    <Badge variant="info" size="sm">{plan.settlement}</Badge>
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                    <div>
                      ₹{plan.price_per_search}/search · balance ₹{plan.balance}
                    </div>
                    <div>
                      Public search{' '}
                      {plan.public_lookup_enabled
                        ? `on, cap ${plan.public_daily_cap}/day (${plan.public_searches_today} used today)`
                        : 'off'}{' '}
                      — the broker&apos;s own setting
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ id: plan.id, data: { is_enabled: !plan.is_enabled } })}
                  >
                    {plan.is_enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(editing === plan.id ? null : plan.id);
                      setDraft({});
                    }}
                  >
                    {editing === plan.id ? 'Cancel' : 'Terms'}
                  </Button>
                </div>
              </div>

              {editing === plan.id && (
                <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
                  <p className="text-xs text-gray-500">
                    Leave a field blank to keep its current value. The balance is
                    set outright, not added to.
                  </p>
                  <Input
                    label="Price per search (₹, what this broker pays us)"
                    placeholder={plan.price_per_search}
                    {...field('price_per_search')}
                  />
                  <Input label="Balance (₹)" placeholder={plan.balance} {...field('balance')} />
                  <Select
                    label="Settlement"
                    options={[
                      { value: '', label: `Keep ${plan.settlement}` },
                      { value: 'prepaid', label: 'Prepaid — refuse when the balance runs out' },
                      { value: 'postpaid', label: 'Postpaid — let charges accrue' },
                    ]}
                    value={draft.settlement ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, settlement: e.target.value }))}
                  />
                  <Button
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => {
                      const payload = Object.fromEntries(
                        Object.entries(draft).filter(([, v]) => v.trim() !== ''),
                      );
                      save.mutate(
                        { id: plan.id, data: payload },
                        { onSuccess: () => { setEditing(null); setDraft({}); } },
                      );
                    }}
                  >
                    Save terms
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
